import React, { useContext, useState } from 'react';
import useForm from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { DHLExportDeclarationInfoInDTO, DHLExportLineItemInDTO } from '@api/ringil-api.v2.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { findErrorMessage } from '@utils/validationUtils.ts';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon.tsx';
import SvgRoute from '@icons/route.svg?react';
import SvgTruck from '@icons/truck.svg?react';
import SvgInfoCircle from '@icons/info.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import InputPriceWithCurrency from '@components/ringil3/Input/InputPriceWithCurrency/InputPriceWithCurrency.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from '@components/framework/input/InputNumber';
import InputQuantityWithUnit from '@components/RingilComponents/form/InputQuantityWithUnit.tsx';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';

interface ComponentProps {
}

interface SidebarData {
  quoteDemandId: string;
  expeditionApplicationId: string;
  carrierName: string;
  data?: DHLExportDeclarationInfoInDTO;
  onContinue: () => void;
  isEditable: boolean;
  isDeletable: boolean;
  reloadData?: () => void;
}

export const useDHLQuoteAdditionalSidebar = () => {
  const [sidebarData, setSidebarData] = useState<SidebarData>(undefined);

  const DHLQuoteAdditionalSidebar: React.FC<ComponentProps> = () => {
    const { saveDHLAdditionalData, deleteDHLAdditionalData } = useContext(ApiContext);
    const { tr } = useTranslationLgs();
    const { i18n } = useTranslation();

    const formSchema = yup.object().shape({
      declaredValue: yup.number().required(tr('Form.declaredValueRequired', 'Declared value is required')),
      declaredValueCurrency: yup.string().required(tr('Form.declaredValueCurrencyRequired', 'Declared value currency is required')),
      lineItems: yup.array().of(
        yup.object().shape({
          description: yup.string().required(tr('Form.descriptionRequired', 'Description is required')),
          quantity: yup
            .number()
            .typeError(tr('Form.quantityMustBeNumber', 'Quantity must be a number'))
            .required(tr('Form.quantityRequired', 'Quantity is required')),
          unitOfMeasurement: yup.string().required(tr('Form.unitRequired', 'Unit is required')),
          piecePrice: yup
            .number()
            .typeError(tr('Form.priceMustBeNumber', 'Price must be a number'))
            .required(tr('Form.priceRequired', 'Price per piece is required')),
          manufacturerCountry: yup.string().required(tr('Form.manufacturerCountryRequired', 'Country code is required')),
          commodityCodeExport: yup.string().required(tr('Form.commodityCodeExportRequired', 'Commodity code for export is required')),
          commodityCodeImport: yup.string().required(tr('Form.commodityCodeImportRequired', 'Commodity code for import is required')),
          netWeight: yup
            .number()
            .typeError(tr('Form.netWeightMustBeNumber', 'Net weight must be a number'))
            .required(tr('Form.netWeightRequired', 'Net weight is required')),
          grossWeight: yup
            .number()
            .typeError(tr('Form.grossWeightMustBeNumber', 'Gross weight must be a number'))
            .required(tr('Form.grossWeightRequired', 'Gross weight is required')),
        }),
      ),
    });

    const getInitialExportLineItem = (): DHLExportLineItemInDTO => ({
      description: '',
      quantity: null,
      unitOfMeasurement: 'PCS',
      piecePrice: null,
      manufacturerCountry: '',
      commodityCodeExport: '',
      commodityCodeImport: '',
      netWeight: null,
      grossWeight: null,
    });

    const initialValues: DHLExportDeclarationInfoInDTO = {
      declaredValue: null,
      declaredValueCurrency: 'CZK',
      lineItems: [getInitialExportLineItem()],
    };

    const form = useForm<DHLExportDeclarationInfoInDTO>(
      formSchema,
      sidebarData?.data || initialValues,
      formData => {
        saveDHLAdditionalData(sidebarData?.quoteDemandId, formData, () => {
          sidebarData?.reloadData();
          setSidebarData(null);
          sidebarData.onContinue();
        });
      },
      false,
      false,
    );

    if (isNullOrUndefined(sidebarData)) return null;

    const totalQuantity = form.values.lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalNetWeight = form.values.lineItems.reduce((sum, item) => sum + (item.netWeight || 0), 0);
    const totalGrossWeight = form.values.lineItems.reduce((sum, item) => sum + (item.grossWeight || 0), 0);
    const totalValue = form.values.lineItems.reduce((sum, item) => sum + (item.quantity || 0) * (item.piecePrice || 0), 0);
    const declaredValueMismatch =
      isNotNullOrUndefined(form.values.declaredValue) && Math.abs(totalValue - form.values.declaredValue) > 0.01;
    const formatNumber = (value: number, maxFractionDigits: number) =>
      value.toLocaleString(i18n.language, { maximumFractionDigits: maxFractionDigits });

    return (
      <RingilSidebar
        visible={isNotNullOrUndefined(sidebarData)}
        heading={tr(`DHLQuoteAdditionalSidebar.title`, `Add additional DHL quote information`)}
        onBack={() => setSidebarData(null)}
        continueLabel={tr(`Form.continueLabel`, `Continue`)}
        onContinue={() => form.validateAndSend()}
        onDeleteForm={sidebarData.isDeletable ? () => {
          form.setValues?.(initialValues);
          form.clear();
          if (!sidebarData?.isEditable) {
            deleteDHLAdditionalData(sidebarData?.quoteDemandId, () => {
              setSidebarData(null);
              sidebarData?.reloadData();
            });
          }
          }
          : null
        }
        showSaveButton={sidebarData?.isEditable}
        maxWidth="1100px"
      >
        <div className="flex flex-col gap-4">
          <VerticalFieldAndValueWithIcon
            label={tr('Form.forExpedition', 'For expedition')}
            valueText={sidebarData.expeditionApplicationId}
            icon={<SvgRoute />}
          />
          <VerticalFieldAndValueWithIcon
            label={tr('Form.selectedCarrier', 'Selected carrier')}
            valueText={sidebarData.carrierName}
            icon={<SvgTruck />}
          />
          <COInlineMessage
            variant={'info'}
            icon={<SvgInfoCircle className="text-content-tertiary" />}
          >
            {tr('QuoteAdditionalInfo.additionalInfoRequiredMessage','The carrier requires this information for the transport of the cargo')}
          </COInlineMessage>
          <COInlineMessage
            variant={'info'}
            icon={<SvgInfoCircle className="text-content-tertiary" />}
          >
            {tr('QuoteAdditionalInfo.printHardCopyShipmentPaperwork','Please note on printing the hardcopy of all the shipment paperwork and affix it to the package.')}
          </COInlineMessage>

          <div className="flex flex-col gap-2">
            <div className="w-[500px]">
              <Field
                label={tr('Form.declaredValue', 'Declared value')}
                required={!form.values.declaredValue}
                errorMessage={findErrorMessage(form.validationErrors, form.names.declaredValue, tr)}
                disabled={!sidebarData?.isEditable}
                skipCurrencyCssMode
              >
                <InputPriceWithCurrency
                  value={form.values.declaredValue}
                  currency={form.values.declaredValueCurrency}
                  changePrice={v => form.setFieldValue(form.names.declaredValue, v)}
                  changeCurrency={v => form.setFieldValue(form.names.declaredValueCurrency, v)}
                />
              </Field>
            </div>
            <div>
              {form.values.lineItems.map((lineItem, index) => (
                <div className={`flex flex-col p-4 pb-6 ${index % 2 === 0 ? 'bg-white' : 'bg-base-quaternary'}`}
                     key={index}>
                  <div className="flex flex-row justify-between items-center w-full">
                    <COHeadline
                      variant={'h4'}
                      title={tr('Form.itemNumber', 'Item nr. {{index}}', { index: index + 1 })}
                    />
                    <RingilButton
                      mode={'outlined-rectangle'}
                      icon={<SvgDelete />}
                      onClick={() => {
                        const newItems = form.values.lineItems.filter((_, i) => i !== index);
                        form.setFieldValue(form.names.lineItems, newItems);
                      }}
                    />
                  </div>
                  <div className="flex flex-row items-center w-full gap-4">
                    <Field
                      label={tr('Form.description', 'Description')}
                      required={!form.values.lineItems[index].description}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.lineItems}[${index}].description`, tr)}
                      disabled={!sidebarData?.isEditable}
                      width={'500px'}
                    >
                      <InputText
                        value={form.values.lineItems[index].description}
                        onChange={e => {
                          const newItems = [...form.values.lineItems];
                          newItems[index] = { ...newItems[index], description: e.target.value };
                          form.setFieldValue(form.names.lineItems, newItems);
                        }}
                      />
                    </Field>
                    <Field
                      label={tr('Form.quantity', 'Quantity')}
                      required={!form.values.lineItems[index].quantity}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.lineItems}[${index}].quantity`, tr)}
                      disabled={!sidebarData?.isEditable}
                      skipCurrencyCssMode
                    >
                      <InputQuantityWithUnit
                        value={form.values.lineItems[index].quantity}
                        unit={form.values.lineItems[index].unitOfMeasurement}
                        changeValue={quantity => {
                          const newItems = [...form.values.lineItems];
                          newItems[index] = { ...newItems[index], quantity };
                          form.setFieldValue(form.names.lineItems, newItems);
                        }}
                        changeUnit={unit => {
                          const newItems = [...form.values.lineItems];
                          newItems[index] = { ...newItems[index], unitOfMeasurement: unit };
                          form.setFieldValue(form.names.lineItems, newItems);
                        }}
                        unitOptions={[
                          { label: tr('Form.unit.pcs', 'PCS'), value: 'PCS' },
                          { label: tr('Form.unit.box', 'BOX'), value: 'BOX' },
                        ]}
                      />
                    </Field>
                    <Field
                      label={tr('Form.pricePerPiece', 'Price per piece')}
                      required={!form.values.lineItems[index].piecePrice}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.lineItems}[${index}].piecePrice`, tr)}
                      disabled={!sidebarData?.isEditable}
                    >
                      <InputNumber
                        value={form.values.lineItems[index].piecePrice}
                        onChange={e => {
                          const newItems = [...form.values.lineItems];
                          newItems[index] = { ...newItems[index], piecePrice: e.value };
                          form.setFieldValue(form.names.lineItems, newItems);
                        }}
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        locale={i18n.language}
                      />
                    </Field>
                    <Field
                      label={tr('Form.countryCode', 'Country code')}
                      required={!form.values.lineItems[index].manufacturerCountry}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.lineItems}[${index}].manufacturerCountry`, tr)}
                      disabled={!sidebarData?.isEditable}
                    >
                      <InputCountry
                        value={form.values.lineItems[index].manufacturerCountry}
                        onChange={v => {
                          const newItems = [...form.values.lineItems];
                          newItems[index] = { ...newItems[index], manufacturerCountry: v };
                          form.setFieldValue(form.names.lineItems, newItems);
                        }}
                      />
                    </Field>
                  </div>

                  <div className="flex flex-row items-center w-full gap-4">
                    <Field
                      label={tr('Form.commodityCodeExport', 'Commodity code for export')}
                      required={!form.values.lineItems[index].commodityCodeExport}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.lineItems}[${index}].commodityCodeExport`, tr)}
                      disabled={!sidebarData?.isEditable}
                      width={'500px'}
                    >
                      <InputText
                        value={form.values.lineItems[index].commodityCodeExport}
                        onChange={e => {
                          const newItems = [...form.values.lineItems];
                          newItems[index] = { ...newItems[index], commodityCodeExport: e.target.value };
                          form.setFieldValue(form.names.lineItems, newItems);
                        }}
                      />
                    </Field>
                    <Field
                      label={tr('Form.commodityCodeImport', 'Commodity code for import')}
                      required={!form.values.lineItems[index].commodityCodeImport}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.lineItems}[${index}].commodityCodeImport`, tr)}
                      disabled={!sidebarData?.isEditable}
                      width={'500px'}
                    >
                      <InputText
                        value={form.values.lineItems[index].commodityCodeImport}
                        onChange={e => {
                          const newItems = [...form.values.lineItems];
                          newItems[index] = { ...newItems[index], commodityCodeImport: e.target.value };
                          form.setFieldValue(form.names.lineItems, newItems);
                        }}
                      />
                    </Field>
                    <Field
                      label={tr('Form.netWeight', 'Net weight')}
                      required={!form.values.lineItems[index].netWeight}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.lineItems}[${index}].netWeight`, tr)}
                      disabled={!sidebarData?.isEditable}
                    >
                      <InputNumber
                        value={form.values.lineItems[index].netWeight}
                        onChange={e => {
                          const newItems = [...form.values.lineItems];
                          newItems[index] = { ...newItems[index], netWeight: e.value };
                          form.setFieldValue(form.names.lineItems, newItems);
                        }}
                        minFractionDigits={0}
                        maxFractionDigits={3}
                        locale={i18n.language}
                      />
                    </Field>
                    <Field
                      label={tr('Form.grossWeight', 'Gross weight')}
                      required={!form.values.lineItems[index].grossWeight}
                      errorMessage={findErrorMessage(form.validationErrors, `${form.names.lineItems}[${index}].grossWeight`, tr)}
                      disabled={!sidebarData?.isEditable}
                    >
                      <InputNumber
                        value={form.values.lineItems[index].grossWeight}
                        onChange={e => {
                          const newItems = [...form.values.lineItems];
                          newItems[index] = { ...newItems[index], grossWeight: e.value };
                          form.setFieldValue(form.names.lineItems, newItems);
                        }}
                        minFractionDigits={0}
                        maxFractionDigits={3}
                        locale={i18n.language}
                      />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <RingilButton
                mode={'link'}
                label={tr('Form.addItem', 'Add item')}
                icon={<SvgPlus />}
                onClick={() => {
                  form.setFieldValue(form.names.lineItems, [...form.values.lineItems, getInitialExportLineItem()]);
                }}
                disabled={!sidebarData?.isEditable}
              />
            </div>
            <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-content-quaternary">
              <COHeadline variant={'h4'} title={tr('DHLQuoteAdditionalSidebar.summary', 'Summary')} />
              <div className="flex flex-row flex-wrap gap-x-8 gap-y-2">
                <div>
                  <div className="text-content-tertiary">{tr('DHLQuoteAdditionalSidebar.totalQuantity', 'Total quantity')}</div>
                  <div>{formatNumber(totalQuantity, 0)}</div>
                </div>
                <div>
                  <div className="text-content-tertiary">{tr('DHLQuoteAdditionalSidebar.totalNetWeight', 'Total net weight')}</div>
                  <div>{formatNumber(totalNetWeight, 3)}</div>
                </div>
                <div>
                  <div className="text-content-tertiary">{tr('DHLQuoteAdditionalSidebar.totalGrossWeight', 'Total gross weight')}</div>
                  <div>{formatNumber(totalGrossWeight, 3)}</div>
                </div>
                <div>
                  <div className="text-content-tertiary">{tr('DHLQuoteAdditionalSidebar.totalValue', 'Total value')}</div>
                  <div>
                    {formatNumber(totalValue, 2)} {form.values.declaredValueCurrency}
                  </div>
                </div>
              </div>
              {declaredValueMismatch && (
                <COInlineMessage variant={'warning'} icon={<SvgInfoCircle className="text-content-tertiary" />}>
                  {tr(
                    'DHLQuoteAdditionalSidebar.declaredValueMismatch',
                    'The total value of items ({{totalValue}} {{currency}}) does not match the declared value ({{declaredValue}} {{currency}}).',
                    {
                      totalValue: formatNumber(totalValue, 2),
                      declaredValue: formatNumber(form.values.declaredValue, 2),
                      currency: form.values.declaredValueCurrency,
                    },
                  )}
                </COInlineMessage>
              )}
            </div>
          </div>
        </div>
      </RingilSidebar>
    )
      ;
  };

  return { DHLQuoteAdditionalSidebar, setSidebarData };
};